/**
 * Materiality Calculation edit helpers for Assessment Reports
 * (category / sensitivity % → overall; planning = 50% of overall; deminimis = 10% of planning).
 */

export function reportsEditSensitivityOptions(category, customCategories = []) {
  const custom = (customCategories || []).find((c) => c?.name === category);
  if (custom && Number.isFinite(Number(custom.percentage))) {
    const base = Number(custom.percentage);
    return [
      { label: 'Conservative', percentage: `${(base / 2).toFixed(1)}%` },
      { label: 'Standard', percentage: `${base.toFixed(1)}%` },
      { label: 'Moderate', percentage: `${(base * 2).toFixed(1)}%` },
    ];
  }
  if (category === 'Net Income') {
    return [
      { label: 'Conservative', percentage: '2.5%' },
      { label: 'Standard', percentage: '5.0%' },
      { label: 'Moderate', percentage: '7.5%' },
    ];
  }
  if (category === 'Total Revenue' || category === 'Total Assets') {
    return [
      { label: 'Conservative', percentage: '0.5%' },
      { label: 'Standard', percentage: '1.0%' },
      { label: 'Moderate', percentage: '2.0%' },
    ];
  }
  return [
    { label: 'Conservative', percentage: '2.5%' },
    { label: 'Standard', percentage: '5.0%' },
    { label: 'Moderate', percentage: '7.5%' },
  ];
}

export function reportsEditBaseAmount(companyProfile, category, customCategories = []) {
  const custom = (customCategories || []).find((c) => c?.name === category);
  if (custom && Number.isFinite(Number(custom.amount))) return Number(custom.amount);
  if (category === 'Net Income') return Number(companyProfile?.netIncome) || 0;
  if (category === 'Total Revenue') return Number(companyProfile?.totalRevenue) || 0;
  if (category === 'Total Assets') return Number(companyProfile?.totalAssets) || 0;
  return Number(companyProfile?.netIncome) || 0;
}

/** Plan-based overall = base × sensitivity rate (no custom threshold override). */
export function reportsEditPlanOverall(companyProfile, category, sensitivityLevel, customCategories = []) {
  const base = reportsEditBaseAmount(companyProfile, category, customCategories);
  const custom = (customCategories || []).find((c) => c?.name === category);
  let rate;
  if (custom && Number.isFinite(Number(custom.percentage))) {
    const basePct = Number(custom.percentage) / 100;
    if (sensitivityLevel === 'Conservative') rate = basePct / 2;
    else if (sensitivityLevel === 'Moderate') rate = basePct * 2;
    else rate = basePct;
  } else if (category === 'Net Income') {
    rate =
      sensitivityLevel === 'Conservative' ? 0.025 :
      sensitivityLevel === 'Moderate' ? 0.075 : 0.05;
  } else if (category === 'Total Revenue' || category === 'Total Assets') {
    rate =
      sensitivityLevel === 'Conservative' ? 0.005 :
      sensitivityLevel === 'Moderate' ? 0.02 : 0.01;
  } else {
    rate = 0.05;
  }
  return base * rate;
}

export function reportsEditSensitivityPercentageLabel(category, sensitivityLevel, customCategories = []) {
  const opt = reportsEditSensitivityOptions(category, customCategories).find(
    (o) => o.label === sensitivityLevel
  );
  return opt?.percentage || '5.0%';
}

function parseMoneyInput(value) {
  if (value == null || String(value).trim() === '') return null;
  const parsed = parseFloat(String(value).replace(/,/g, ''));
  return Number.isFinite(parsed) ? parsed : null;
}

export function reportsEditResolvedThresholds({
  companyProfile,
  category,
  sensitivityLevel,
  customCategories,
  customOverallMateriality,
  customPlanningMateriality,
  customDeminimis,
}) {
  let overall = parseMoneyInput(customOverallMateriality);
  if (overall == null) {
    overall = reportsEditPlanOverall(companyProfile, category, sensitivityLevel, customCategories);
  }
  let planning = parseMoneyInput(customPlanningMateriality);
  if (planning == null) planning = overall * 0.5;
  let deminimis = parseMoneyInput(customDeminimis);
  if (deminimis == null) deminimis = planning * 0.1;
  return { overallMateriality: overall, planningMateriality: planning, deminimis };
}

/**
 * Build updated materiality_json for PUT (category, sensitivity, thresholds, custom fields).
 */
export function reportsEditBuildMaterialityJson(baseMaterialityJson, draft) {
  const {
    selectedCategory,
    sensitivityLevel,
    customCategories,
    customOverallMateriality,
    customPlanningMateriality,
    customDeminimis,
    overrideReasoning,
  } = draft;

  const thresholds = reportsEditResolvedThresholds({
    companyProfile: baseMaterialityJson?.companyProfile,
    category: selectedCategory,
    sensitivityLevel,
    customCategories,
    customOverallMateriality,
    customPlanningMateriality,
    customDeminimis,
  });

  const sensitivityPercentage = reportsEditSensitivityPercentageLabel(
    selectedCategory,
    sensitivityLevel,
    customCategories
  );

  const hasCustomThreshold =
    parseMoneyInput(customOverallMateriality) != null ||
    parseMoneyInput(customPlanningMateriality) != null ||
    parseMoneyInput(customDeminimis) != null;

  const prevConfirmed = baseMaterialityJson?.confirmedMateriality || {};

  // When overrides are cleared (Reset), clear override fields too — do not keep prior saved values.
  const nextOverrideAmount =
    hasCustomThreshold && parseMoneyInput(customOverallMateriality) != null
      ? String(customOverallMateriality)
      : '';
  const nextOverrideReasoning = hasCustomThreshold ? String(overrideReasoning || '') : '';

  return {
    ...baseMaterialityJson,
    selectedCategory,
    sensitivityLevel,
    sensitivityPercentage,
    customCategories: Array.isArray(customCategories) ? customCategories : [],
    overrideAmount: nextOverrideAmount,
    overrideReasoning: nextOverrideReasoning,
    confirmedMateriality: {
      ...prevConfirmed,
      overallMateriality: thresholds.overallMateriality,
      planningMateriality: thresholds.planningMateriality,
      deminimis: thresholds.deminimis,
      selectedCategory,
      sensitivityLevel,
      overrideAmount: nextOverrideAmount,
      overrideReasoning: nextOverrideReasoning,
      customOverallMateriality: customOverallMateriality != null ? String(customOverallMateriality) : '',
      customPlanningMateriality: customPlanningMateriality != null ? String(customPlanningMateriality) : '',
      customDeminimis: customDeminimis != null ? String(customDeminimis) : '',
      customCategories: Array.isArray(customCategories) ? customCategories : [],
    },
  };
}
